<template>
  <div class="subscriptions-list">
    <!-- Empty State -->
    <div
      v-if="!subscriptions || subscriptions.length === 0"
      class="empty-state"
    >
      <div class="empty-icon">
        <i class="fas fa-folder-open"></i>
      </div>
      <h4>{{ $t("qbank.no_subscriptions") }}</h4>
      <p>{{ $t("qbank.start_journey") }}</p>
    </div>

    <!-- عرض قائمة الاشتراكات -->
    <div
      v-for="subscription in subscriptions"
      :key="subscription.id"
      class="subscription-card"
      :class="{ expired: !isActive(subscription.is_valid) }"
    >
      <div class="subscription-content">
        <!-- رمز الاشتراك -->
        <div
          class="subscription-icon"
          :class="isActive(subscription.is_valid) ? 'active' : 'expired'"
        >
          <i
            :class="
              isActive(subscription.is_valid) ? 'fas fa-check' : 'fas fa-times'
            "
          ></i>
        </div>

        <!-- تفاصيل الاشتراك -->
        <div class="subscription-info">
          <h5
            class="subscription-name"
            @click="
              isActive(subscription.is_valid) &&
                fetchSubscriptionDetails(subscription.id)
            "
          >
            {{ subscription.name }}
          </h5>
          <div class="subscription-status">
            <span
              class="status-badge"
              :class="isActive(subscription.is_valid) ? 'active' : 'expired'"
            >
              <i
                :class="
                  isActive(subscription.is_valid)
                    ? 'fas fa-calendar-check'
                    : 'fas fa-calendar-times'
                "
              ></i>
              {{
                isActive(subscription.is_valid)
                  ? $t("qbank.active")
                  : $t("qbank.expired")
              }}
            </span>
            <span class="date-text">
              {{
                isActive(subscription.is_valid)
                  ? `${$t("qbank.until")} ${formatDate(subscription.end_date)}`
                  : `${$t("qbank.expired_on")} ${formatDate(
                      subscription.end_date
                    )}`
              }}
            </span>
          </div>
        </div>

        <!-- إجراءات -->
        <div class="subscription-action">
          <button
            v-if="isActive(subscription.is_valid)"
            class="action-btn primary"
            @click="fetchSubscriptionDetails(subscription.id)"
          >
            <i class="fas fa-play text-white"></i>
            <span>{{ $t("qbank.test_yourself") }}</span>
          </button>
          <button
            v-else
            class="action-btn secondary"
            @click="confirmSubscription(subscription.id)"
          >
            <i class="fas fa-redo"></i>
            <span>{{ $t("qbank.renew") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl, baseUrl } from "@/utils/api";
import dayjs from "dayjs";

export default {
  props: {
    subscriptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      tests: null,
      token: localStorage.getItem("access_token"),
      subscriptionId: null,
    };
  },
  methods: {
    toggleDropdown() {
      const dropdownMenu = document.querySelector(
        "#custom-dropdown .dropdown-menu"
      );
      if (dropdownMenu.style.display === "block") {
        dropdownMenu.style.display = "none";
      } else {
        dropdownMenu.style.display = "block";
      }
    },
    async fetchSubscriptionDetails(subscriptionId) {
      window.location.href = "qbank-list-quiz?subscriptionId=" + subscriptionId;
    },
    getStyle(is_active) {
      const bgColor = this.isActive(is_active) ? "#07c232" : "#f92828";
      return `background-color: ${bgColor} !important; color: #fff !important;`;
    },
    isActive(is_active) {
      return is_active;
    },
    formatDate(date) {
      return dayjs(date).format("MM/DD/YYYY");
    },
    async confirmSubscription(planId) {
      const result = await Swal.fire({
        title: this.$t("qbank.confirm_renew_title"),
        text: this.$t("qbank.confirm_renew_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3ddc97",
        cancelButtonColor: "#f46a6a",
        cancelButtonText: this.$t("common.cancel"),
        confirmButtonText: this.$t("common.ok"),
      });

      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${apiUrl}/profile/qbanks/subscription-plans/${planId}/subscribe`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this.token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.ok) {
            Swal.fire({
              title: this.$t("qbank.renew_success"),
              icon: "success",
              confirmButtonText: this.$t("common.ok"),
            }).then(() => {
              location.reload(); // إعادة تحميل الصفحة لتحديث البيانات
            });
          } else {
            const error = await response.json();
            Swal.fire({
              title: this.$t("common.error"),
              text: error.message || this.$t("qbank.renew_error"),
              icon: "error",
              confirmButtonText: this.$t("common.ok"),
            });
          }
        } catch (error) {
          Swal.fire({
            title: this.$t("common.error"),
            text: this.$t("qbank.cannot_renew"),
            icon: "error",
            confirmButtonText: this.$t("common.ok"),
          });
        }
      }
    },
  },
};
</script>

<style scoped>
/* ========== Subscriptions List ========== */
.subscriptions-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ========== Empty State ========== */
.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #e8fff1 0%, #d4f7e4 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
}

.empty-icon i {
  font-size: 28px;
  color: #00c744;
}

.empty-state h4 {
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 8px;
}

.empty-state p {
  color: #718096;
  font-size: 0.9rem;
  margin: 0;
}

/* ========== Subscription Card ========== */
.subscription-card {
  background: linear-gradient(135deg, #f8fffe 0%, #ffffff 100%);
  border: 1px solid rgba(0, 199, 68, 0.12);
  border-radius: 16px;
  padding: 16px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.subscription-card::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #00c744 0%, #00a83b 100%);
  border-radius: 0 16px 16px 0;
}

.subscription-card:hover {
  transform: translateX(-4px);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.12);
}

.subscription-card.expired {
  background: linear-gradient(135deg, #fefefe 0%, #f9f9f9 100%);
  border-color: rgba(0, 0, 0, 0.06);
}

.subscription-card.expired::before {
  background: linear-gradient(180deg, #f46a6a 0%, #e55555 100%);
}

/* ========== Subscription Content ========== */
.subscription-content {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* ========== Subscription Icon ========== */
.subscription-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.subscription-icon.active {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}

.subscription-icon.expired {
  background: linear-gradient(135deg, #f46a6a 0%, #e55555 100%);
  box-shadow: 0 4px 15px rgba(244, 106, 106, 0.3);
}

.subscription-icon i {
  font-size: 18px;
  color: white;
}

/* ========== Subscription Info ========== */
.subscription-info {
  flex: 1;
  min-width: 0;
}

.subscription-name {
  color: #2d3748;
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 6px 0;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subscription-name:hover {
  color: #00c744;
}

.subscription-status {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.active {
  background: rgba(0, 199, 68, 0.1);
  color: #00a83b;
}

.status-badge.expired {
  background: rgba(244, 106, 106, 0.1);
  color: #e55555;
}

.status-badge i {
  font-size: 0.7rem;
}

.date-text {
  color: #718096;
  font-size: 0.8rem;
}

/* ========== Action Button ========== */
.subscription-action {
  flex-shrink: 0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn.primary {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}

.action-btn.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 199, 68, 0.4);
}

.action-btn.secondary {
  background: linear-gradient(135deg, #f46a6a 0%, #e55555 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(244, 106, 106, 0.3);
}

.action-btn.secondary i {
  color: white;
}

.action-btn.secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(244, 106, 106, 0.4);
}

.action-btn i {
  font-size: 0.8rem;
}

/* ========== Responsive ========== */
@media (max-width: 576px) {
  .subscription-content {
    flex-wrap: wrap;
  }

  .subscription-info {
    flex: 1 1 calc(100% - 60px);
  }

  .subscription-action {
    width: 100%;
    margin-top: 10px;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }
}

/* ========== Dark Mode Support ========== */
[data-bs-theme="dark"] .empty-state,
[data-theme="dark"] .empty-state,
.dark .empty-state {
  background: transparent;
}

[data-bs-theme="dark"] .empty-icon,
[data-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: linear-gradient(135deg, #0f3d1c 0%, #1a5c2e 100%);
}

[data-bs-theme="dark"] .empty-state h4,
[data-theme="dark"] .empty-state h4,
.dark .empty-state h4 {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .empty-state p,
[data-theme="dark"] .empty-state p,
.dark .empty-state p {
  color: #94a3b8;
}

[data-bs-theme="dark"] .subscription-card,
[data-theme="dark"] .subscription-card,
.dark .subscription-card {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-color: rgba(255, 255, 255, 0.08);
}

[data-bs-theme="dark"] .subscription-card:hover,
[data-theme="dark"] .subscription-card:hover,
.dark .subscription-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .subscription-card.expired,
[data-theme="dark"] .subscription-card.expired,
.dark .subscription-card.expired {
  background: linear-gradient(135deg, #1e293b 0%, #1a1a2e 100%);
  border-color: rgba(255, 255, 255, 0.05);
}

[data-bs-theme="dark"] .subscription-name,
[data-theme="dark"] .subscription-name,
.dark .subscription-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .subscription-name:hover,
[data-theme="dark"] .subscription-name:hover,
.dark .subscription-name:hover {
  color: #4ade80;
}

[data-bs-theme="dark"] .status-badge.active,
[data-theme="dark"] .status-badge.active,
.dark .status-badge.active {
  background: rgba(74, 222, 128, 0.15);
  color: #4ade80;
}

[data-bs-theme="dark"] .status-badge.expired,
[data-theme="dark"] .status-badge.expired,
.dark .status-badge.expired {
  background: rgba(244, 106, 106, 0.15);
  color: #f87171;
}

[data-bs-theme="dark"] .date-text,
[data-theme="dark"] .date-text,
.dark .date-text {
  color: #94a3b8;
}

[data-bs-theme="dark"] .subscription-icon.active,
[data-theme="dark"] .subscription-icon.active,
.dark .subscription-icon.active {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .subscription-icon.expired,
[data-theme="dark"] .subscription-icon.expired,
.dark .subscription-icon.expired {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .action-btn.primary,
[data-theme="dark"] .action-btn.primary,
.dark .action-btn.primary {
  background: linear-gradient(135deg, #0f5132 0%, #0a3622 100%);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .action-btn.secondary,
[data-theme="dark"] .action-btn.secondary,
.dark .action-btn.secondary {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}
</style>
